import React, { useEffect, useState } from "react";
import { LegalShell, Section } from "./LegalShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Mail, Trash2, ShieldCheck, Clock } from "lucide-react";

export default function KontoLoeschenPage() {
  const [email, setEmail] = useState("");
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    document.title = "Plantus · Konto und Daten löschen";
  }, []);

  const handleMailto = (e) => {
    e.preventDefault();
    if (!email) {
      toast.error("Bitte gib deine E-Mail-Adresse ein.");
      return;
    }
    setSubmitting(true);
    const subject = encodeURIComponent("Antrag auf Löschung meines Plantus-Kontos");
    const body = encodeURIComponent(
      `Hiermit beantrage ich die Löschung meines Plantus-Kontos und aller zugehörigen Daten.\n\n` +
        `E-Mail-Adresse des Kontos: ${email}\n\n` +
        `Grund (optional): ${reason || ","}\n\n` +
        `Ich bestätige, dass ich der rechtmäßige Inhaber dieses Kontos bin.\n\n` +
        `Mit freundlichen Grüßen`
    );
    window.location.href = `mailto:info@plantus-app.com?subject=${subject}&body=${body}`;
    setTimeout(() => {
      setSubmitting(false);
      toast.success("E-Mail-Programm geöffnet. Bitte sende die vorbereitete Nachricht ab.");
    }, 800);
  };

  return (
    <LegalShell
      title="Konto und Daten löschen"
      lastUpdated="Februar 2026"
      testId="konto-loeschen-page"
    >
      <Section title="Löschung Ihres Plantus-Kontos beantragen">
        <p>
          Sie können jederzeit die vollständige Löschung Ihres Plantus-Kontos und aller
          zugehörigen personenbezogenen Daten anfordern. Nutzen Sie dazu das folgende Formular
          oder senden Sie uns eine E-Mail an{" "}
          <a href="mailto:info@plantus-app.com" className="text-forest-700 hover:underline">
            info@plantus-app.com
          </a>.
        </p>
      </Section>

      <Section title="Welche Daten werden gelöscht?">
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="rounded-lg border border-forest-100 bg-forest-50/50 p-4">
            <div className="flex items-center gap-2 font-semibold text-forest-900">
              <Trash2 className="h-4 w-4" /> Vollständig gelöscht
            </div>
            <ul className="mt-2 list-disc pl-5 text-sm text-neutral-700">
              <li>Nutzerkonto (Name, E-Mail, Passwort-Hash)</li>
              <li>Betriebsdaten und Firmenprofil</li>
              <li>Felder und Flächen</li>
              <li>Pflanzenschutz-Anwendungen und Tankmischungen</li>
              <li>Hochgeladene Fotos und OCR-Notizen</li>
              <li>Team-Mitglieder-Einladungen</li>
            </ul>
          </div>
          <div className="rounded-lg border border-amber-100 bg-amber-50/50 p-4">
            <div className="flex items-center gap-2 font-semibold text-amber-900">
              <ShieldCheck className="h-4 w-4" /> Aus rechtlichen Gründen aufbewahrt
            </div>
            <ul className="mt-2 list-disc pl-5 text-sm text-neutral-700">
              <li>Rechnungen (10 Jahre gemäß § 147 AO)</li>
              <li>Zahlungsdaten bei Stripe (bis zur gesetzlichen Frist)</li>
              <li>Anonymisierte Log-Daten (max. 30 Tage)</li>
            </ul>
          </div>
        </div>
      </Section>

      <Section title="Bearbeitungszeit">
        <div className="flex items-start gap-3 rounded-lg border border-neutral-200 bg-neutral-50 p-4">
          <Clock className="mt-0.5 h-5 w-5 text-neutral-600" />
          <div>
            <p className="font-medium text-neutral-900">
              Wir bearbeiten Löschanfragen innerhalb von 30 Tagen.
            </p>
            <p className="mt-1 text-sm text-neutral-600">
              Sie erhalten eine Bestätigung per E-Mail, sobald die Löschung abgeschlossen ist.
              Bei Fragen antworten wir werktags meist innerhalb von 24 Stunden.
            </p>
          </div>
        </div>
      </Section>

      <Section title="Löschung anfordern">
        <form
          onSubmit={handleMailto}
          className="space-y-4 rounded-lg border border-neutral-200 bg-white p-5"
          data-testid="konto-loeschen-form"
        >
          <div>
            <Label htmlFor="email">E-Mail-Adresse deines Plantus-Kontos *</Label>
            <Input
              id="email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="beispiel@betrieb.de"
              className="mt-1"
              data-testid="konto-loeschen-email-input"
            />
          </div>
          <div>
            <Label htmlFor="reason">Grund (optional)</Label>
            <Textarea
              id="reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Warum möchtest du dein Konto löschen? (freiwillig)"
              className="mt-1"
              rows={3}
              data-testid="konto-loeschen-reason-input"
            />
          </div>
          <Button
            type="submit"
            disabled={submitting}
            className="w-full bg-forest-700 hover:bg-forest-800"
            data-testid="konto-loeschen-submit-btn"
          >
            <Mail className="mr-2 h-4 w-4" />
            {submitting ? "Öffne E-Mail-Programm.." : "Löschung per E-Mail beantragen"}
          </Button>
          <p className="text-xs text-neutral-500">
            Alternativ direkt schreiben an:{" "}
            <a href="mailto:info@plantus-app.com" className="text-forest-700 hover:underline">
              info@plantus-app.com
            </a>
          </p>
        </form>
      </Section>

      <Section title="Rechtsgrundlage">
        <p>
          Ihr Recht auf Löschung ergibt sich aus Art. 17 DSGVO („Recht auf Vergessenwerden"). Wir
          setzen Ihre Anfrage entsprechend der gesetzlichen Fristen um. Weitere Informationen
          finden Sie in unserer{" "}
          <a href="/datenschutz" className="text-forest-700 hover:underline">
            Datenschutzerklärung
          </a>.
        </p>
      </Section>
    </LegalShell>
  );
}
